import React from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

export const CHART_THEME_COLORS = {
  primary: "#1D4ED8",
  success: "#16A34A",
  warning: "#D97706",
  danger: "#DC2626",
  info: "#2563EB",
  purple: "#7C3AED",
  cyan: "#0891B2",
  grid: "#F1F5F9",
  axis: "#94A3B8",
  axisLabel: "#64748B",
};

export function EmptyChartState({ message = "No data available." }) {
  return (
    <div
      style={{
        height: "100%",
        minHeight: 140,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#64748B",
        fontSize: 12,
        textAlign: "center",
        padding: "12px",
      }}
    >
      {message}
    </div>
  );
}

export function ChartCard({
  title,
  subtitle,
  headerRight,
  height = 220,
  className = "rca-chart-card",
  children,
  style,
}) {
  return (
    <div className={className} style={style}>
      {(title || subtitle || headerRight) && (
        <div
          className={`${className}__head`}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 8,
          }}
        >
          <div>
            {title && (
              <div className={`${className}__title`} style={{ fontWeight: 700 }}>
                {title}
              </div>
            )}
            {subtitle && (
              <div className={`${className}__subtitle`} style={{ fontSize: 11, color: "#64748B" }}>
                {subtitle}
              </div>
            )}
          </div>
          {headerRight && <div>{headerRight}</div>}
        </div>
      )}
      {height ? (
        <ResponsiveContainer width="100%" height={height}>
          {children}
        </ResponsiveContainer>
      ) : (
        children
      )}
    </div>
  );
}

export function DonutChart({
  data = [],
  innerRadius = 70,
  outerRadius = 100,
  paddingAngle = 2,
  height = 220,
  centerLabel,
  emptyMessage = "No data available yet.",
  tooltipFormatter,
  className,
  style,
}) {
  const hasData =
    Array.isArray(data) &&
    data.length > 0 &&
    data.some((d) => Number(d.value ?? d.count ?? 0) > 0);

  if (!hasData) {
    return <EmptyChartState message={emptyMessage} />;
  }

  return (
    <div
      style={{ position: "relative", width: "100%", height, ...style }}
      className={className}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey={(entry) => Number(entry.value ?? entry.count ?? 0)}
            nameKey={(entry) => entry.name ?? entry.label ?? ""}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={paddingAngle}
            startAngle={90}
            endAngle={-270}
          >
            {data.map((entry, index) => (
              <Cell
                key={entry.name || entry.label || `cell-${index}`}
                fill={entry.color || entry.fill || CHART_THEME_COLORS.primary}
              />
            ))}
          </Pie>
          <RechartsTooltip
            content={
              tooltipFormatter
                ? tooltipFormatter
                : ({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const item = payload[0].payload;
                    return (
                      <div
                        style={{
                          background: "#fff",
                          border: "1px solid #E2E8F0",
                          borderRadius: 8,
                          padding: "6px 10px",
                          boxShadow: "0 4px 12px rgba(15, 23, 42, 0.1)",
                          fontSize: 12,
                        }}
                      >
                        <strong>{item.name || item.label}</strong>
                        <div style={{ color: "#475569", marginTop: 2 }}>
                          {Number(item.value ?? item.count ?? 0).toLocaleString()}
                        </div>
                      </div>
                    );
                  }
            }
          />
        </PieChart>
      </ResponsiveContainer>
      {centerLabel && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          {centerLabel.primary && (
            <strong
              style={{
                fontSize: centerLabel.primarySize || 28,
                color: "#10245A",
                lineHeight: 1.1,
              }}
            >
              {centerLabel.primary}
            </strong>
          )}
          {centerLabel.secondary && (
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: centerLabel.secondaryColor || "#16A34A",
                marginTop: 4,
              }}
            >
              {centerLabel.secondary}
            </span>
          )}
          {centerLabel.sub && (
            <small style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
              {centerLabel.sub}
            </small>
          )}
        </div>
      )}
    </div>
  );
}

export function BarChartComponent({
  data = [],
  layout = "horizontal",
  dataKey = "count",
  categoryKey = "name",
  barColor = CHART_THEME_COLORS.primary,
  barSize = 22,
  barRadius,
  height = 210,
  emptyMessage = "No data available yet.",
  margin,
  categoryWidth = 140,
}) {
  const hasData = Array.isArray(data) && data.length > 0;
  if (!hasData) {
    return <EmptyChartState message={emptyMessage} />;
  }

  const defaultRadius =
    barRadius ?? (layout === "vertical" ? [0, 4, 4, 0] : [4, 4, 0, 0]);
  const defaultMargin =
    margin ?? (layout === "vertical" ? { left: 8, right: 16 } : { left: -18, right: 8 });

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout={layout} margin={defaultMargin}>
        <CartesianGrid
          horizontal={layout !== "vertical"}
          vertical={layout === "vertical"}
          stroke={CHART_THEME_COLORS.grid}
        />
        {layout === "vertical" ? (
          <>
            <XAxis
              type="number"
              tick={{ fontSize: 10, fill: CHART_THEME_COLORS.axis }}
              allowDecimals={false}
            />
            <YAxis
              type="category"
              dataKey={categoryKey}
              width={categoryWidth}
              tick={{ fontSize: 10, fill: CHART_THEME_COLORS.axisLabel }}
            />
          </>
        ) : (
          <>
            <XAxis
              dataKey={categoryKey}
              tick={{ fontSize: 10, fill: CHART_THEME_COLORS.axis }}
            />
            <YAxis
              tick={{ fontSize: 10, fill: CHART_THEME_COLORS.axis }}
              allowDecimals={false}
            />
          </>
        )}
        <RechartsTooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
        <Bar
          dataKey={dataKey}
          fill={barColor}
          radius={defaultRadius}
          barSize={barSize}
        >
          {data.map((entry, index) =>
            entry.color ? <Cell key={`cell-${index}`} fill={entry.color} /> : null
          )}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function AreaTimeSeriesChart({
  data = [],
  dataKey = "count",
  dateKey = "date",
  color = CHART_THEME_COLORS.primary,
  gradientId = "chartAreaGradient",
  height = 210,
  emptyMessage = "No time-series data available yet.",
  margin = { left: -18, right: 8 },
  minTickGap = 24,
}) {
  const hasData = Array.isArray(data) && data.length > 0;
  if (!hasData) {
    return <EmptyChartState message={emptyMessage} />;
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={margin}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.25} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke={CHART_THEME_COLORS.grid} />
        <XAxis
          dataKey={dateKey}
          tick={{ fontSize: 9, fill: CHART_THEME_COLORS.axis }}
          tickFormatter={(value) => String(value).slice(5)}
          minTickGap={minTickGap}
        />
        <YAxis
          tick={{ fontSize: 10, fill: CHART_THEME_COLORS.axis }}
          allowDecimals={false}
        />
        <RechartsTooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
        <Area
          type="monotone"
          dataKey={dataKey}
          stroke={color}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
